import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, LogOut } from "lucide-react";
import { LINKS, ARC } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import PageHeader from "@/components/PageHeader";
import { useMizuWallet, useWalletUI, shortAddress } from "@/components/Wallet";
import { GridBackground } from "@/components/Effects";

export default function Connect() {
  const { t } = useLanguage();
  const { openModal } = useWalletUI();
  const w = useMizuWallet();

  const rows = [
    [t("connectPage.networkLabel"), ARC.networkLabel],
    [t("connectPage.chainId"), String(ARC.chainId)],
    [t("connectPage.asset"), ARC.symbol],
  ];

  return (
    <div data-testid="connect-page" className="relative overflow-hidden">
      <GridBackground />
      <PageHeader
        label={t("connectPage.label")}
        title={t("connectPage.title")}
        sub={t("connectPage.sub")}
      />

      <section className="relative mx-auto max-w-3xl px-4 pb-24 pt-12 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mizu-panel mizu-corners p-6 sm:p-8"
          data-testid="connect-terminal"
        >
          <div className="mb-6 flex items-center justify-between border-b border-mizu-cyan/15 pb-3">
            <span className="font-mono text-[10px] tracking-[0.3em] text-mizu-cyan">
              {t("connectPage.label")}
            </span>
            <span
              className={`flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] ${
                w.isConnected && !w.wrongNetwork ? "text-mizu-cyan" : "text-mizu-faint"
              }`}
              data-testid="connect-status-chip"
            >
              <span
                className={`h-1.5 w-1.5 ${
                  w.isConnected && !w.wrongNetwork
                    ? "animate-pulse-glow bg-mizu-cyan"
                    : "bg-mizu-faint"
                }`}
              />
              {w.isConnected && !w.wrongNetwork
                ? t("connectPage.connected")
                : t("connectPage.notConnected")}
            </span>
          </div>

          {/* network rows */}
          <div className="mb-6">
            {rows.map(([k, v]) => (
              <div
                key={k}
                className="flex items-center justify-between border-b border-mizu-cyan/8 py-3 font-mono"
              >
                <span className="text-[10px] tracking-[0.3em] text-mizu-faint">{k}</span>
                <span className="text-xs tracking-[0.15em] text-mizu-silver">{v}</span>
              </div>
            ))}
          </div>

          {!w.isConnected ? (
            <button
              data-testid="connect-page-connect"
              onClick={openModal}
              className="mizu-btn w-full border border-mizu-cyan bg-mizu-cyan/15 px-6 py-4 font-mono text-sm font-semibold tracking-[0.25em] text-white"
            >
              [ {t("connectPage.connectWallet")} ]
            </button>
          ) : (
            <div className="space-y-4" data-testid="connect-connected-panel">
              <div className="border border-mizu-cyan/15 bg-[#020611]/70 p-4 font-mono">
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-[10px] tracking-[0.3em] text-mizu-faint">
                    {t("connectPage.wallet")}
                  </span>
                  <span className="text-xs text-white" data-testid="connect-address">
                    {shortAddress(w.address)}
                  </span>
                </div>
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-[10px] tracking-[0.3em] text-mizu-faint">
                    {t("connectPage.networkLabel")}
                  </span>
                  <span className={`text-xs ${w.wrongNetwork ? "text-red-400" : "text-mizu-cyan"}`}>
                    {w.wrongNetwork ? `CHAIN ${w.chainId}` : ARC.networkLabel}
                  </span>
                </div>
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-[10px] tracking-[0.3em] text-mizu-faint">
                    {t("connectPage.statusLabel")}
                  </span>
                  <span className={`text-xs ${w.wrongNetwork ? "text-red-400" : "text-mizu-cyan"}`}>
                    {w.wrongNetwork ? "!" : t("connectPage.connected")}
                  </span>
                </div>
              </div>

              {w.wrongNetwork && (
                <div className="border border-red-400/30 bg-red-950/20 p-4" data-testid="connect-wrong-network">
                  <p className="font-mono text-xs text-red-400">
                    {t("connectPage.wrongNetwork")}
                  </p>
                  <button
                    data-testid="connect-switch-arc"
                    disabled={w.switching}
                    onClick={w.switchToArc}
                    className="mizu-btn mt-3 w-full border border-mizu-cyan/50 bg-mizu-cyan/10 px-4 py-3 font-mono text-xs tracking-[0.25em] text-mizu-cyan disabled:opacity-50"
                  >
                    [ {w.switching ? t("wallet.switching") : t("connectPage.switchToArc")} ]
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <a
                  href={`${LINKS.explorer}/address/${w.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="connect-view-explorer"
                  className="mizu-btn flex items-center justify-center gap-2 border border-mizu-cyan/40 px-4 py-3 font-mono text-xs tracking-[0.2em] text-mizu-cyan"
                >
                  {t("connectPage.viewOnExplorer")} <ExternalLink size={11} />
                </a>
                <button
                  data-testid="connect-disconnect"
                  onClick={() => w.disconnect()}
                  className="mizu-btn flex items-center justify-center gap-2 border border-mizu-cyan/25 px-4 py-3 font-mono text-xs tracking-[0.2em] text-mizu-dim hover:text-white"
                >
                  {t("connectPage.disconnect")} <LogOut size={11} />
                </button>
              </div>
            </div>
          )}
        </motion.div>

        <p className="mt-6 text-center font-mono text-[10px] leading-6 text-mizu-faint">
          &gt; MIZUKARA never asks for seed phrases or private keys.
          <span className="animate-blink">_</span>
        </p>
      </section>
    </div>
  );
}
